// src/services/reportPeriods.js
//
// Which days a weekly report covers.
//
// A report covers one ISO week (Monday to Sunday), CUT AT THE MONTH BOUNDARY,
// because goal progress restarts on the 1st: a report must never mix two
// months. A week that runs Mon Sep 28 – Sun Oct 4 is therefore two report
// periods, "Sep 28 – 30" (September) and "Oct 1 – 4" (October). The month-end
// reminder (reportReminderSchedule.js) asks for the first half before the 1st.
//
// `weekOf` uses enrollmentPeriods.weekKey, the ISO year/week (the old form used
// the calendar year, which mislabelled the first days of January).
//
// Pure and Firebase-free so it is unit-tested (reportPeriods.test.js).

import { weekKey, monthKey, monthLabel } from "./enrollmentPeriods";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");
const DAY_MS = 24 * 60 * 60 * 1000;

const atMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** "YYYY-MM-DD" in local time. */
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Local midnight for "YYYY-MM-DD", or null. */
export const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const mondayOf = (date) => {
  const d = atMidnight(date);
  const isoDay = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() - (isoDay - 1));
  return d;
};

const label = (start, end) => {
  const sm = MONTHS[start.getMonth()];
  const em = MONTHS[end.getMonth()];
  const range =
    start.getTime() === end.getTime()
      ? `${sm} ${start.getDate()}`
      : sm === em
      ? `${sm} ${start.getDate()} – ${end.getDate()}`
      : `${sm} ${start.getDate()} – ${em} ${end.getDate()}`;
  return `${range}, ${end.getFullYear()}`;
};

const segment = (start, end, partOfSplitWeek) => ({
  key: ymd(start), // periodStart doubles as a stable id
  weekOf: weekKey(start),
  monthId: monthKey(start),
  monthLabel: monthLabel(monthKey(start)),
  periodStart: ymd(start),
  periodEnd: ymd(end),
  label: label(start, end),
  partOfSplitWeek,
});

/** The one or two report periods of the ISO week containing `date`. */
export const weekSegments = (date) => {
  const monday = mondayOf(date);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  if (monday.getMonth() === sunday.getMonth()) return [segment(monday, sunday, false)];
  const monthEnd = new Date(monday.getFullYear(), monday.getMonth() + 1, 0);
  const nextStart = new Date(monthEnd.getFullYear(), monthEnd.getMonth(), monthEnd.getDate() + 1);
  return [segment(monday, monthEnd, true), segment(nextStart, sunday, true)];
};

/** The report period containing `date`. */
export const segmentFor = (date) => {
  const day = ymd(atMidnight(date));
  return weekSegments(date).find((s) => s.periodStart <= day && day <= s.periodEnd);
};

/** The period with this key (its start date), or null. */
export const segmentByKey = (key) => {
  const d = parseYmd(key);
  if (!d) return null;
  const s = segmentFor(d);
  return s && s.key === key ? s : null;
};

/**
 * What the period picker offers, the default first:
 *   - the period today falls in (default)
 *   - the other half of this week when it is split and that half has started
 *   - on Monday and Tuesday, last week's period(s), for a report filed late
 */
export const segmentOptions = (now = new Date()) => {
  const today = atMidnight(now instanceof Date ? now : new Date(now));
  const todayKey = ymd(today);
  const current = segmentFor(today);
  const out = [current];
  weekSegments(today)
    .filter((s) => s.key !== current.key && s.periodStart <= todayKey)
    .forEach((s) => out.push(s));
  const isoDay = today.getDay() === 0 ? 7 : today.getDay();
  if (isoDay <= 2) {
    weekSegments(new Date(today.getTime() - 7 * DAY_MS))
      .reverse()
      .forEach((s) => out.push(s));
  }
  const seen = new Set();
  return out.filter((s) => (seen.has(s.key) ? false : seen.add(s.key)));
};
